import { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import type { SupplierAccess } from "@shared-core/data/supplier-portal-adapter";
import { useSupplierPortalAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { linkTokenFromFragment } from "@shared-core/domain/supplier-link-fragment";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { useLoad } from "@shared-core/domain/useLoad";
import { QUOTE_REQUEST_WORDING } from "@shared-core/domain/supplier-quote-request";
import { ACTION_WORDING } from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { LinkShell } from "../shell/LinkShell";
import { SignedInShell } from "../shell/SignedInShell";
import { useSupplierSession } from "../shell/session";
import { useIsStaffView, useNotFoundWhy } from "../shell/staffView";
import { EmptyState, LoadFailed, LoadingCard } from "../shell/states";
import { QuoteRequestBody } from "./QuoteRequestBody";

/**
 * `/quote-requests/:supplierQuoteNo`: one quote request, two ways in (spec 33). With an emailed link's token in the
 * #fragment it opens in the link chrome, scoped to this request alone; without one it opens inside the signed-in shell.
 */
export function QuoteRequestPage() {
  const { supplierQuoteNo = "" } = useParams<{ supplierQuoteNo: string }>();
  const { hash, pathname } = useLocation();
  // A staff view is never a link: a pasted #fragment must not draw the link chrome (spec 05 §4.5).
  const isStaffView = useIsStaffView();
  const token = isStaffView ? null : linkTokenFromFragment(hash);

  if (token) {
    return (
      <LinkShell returnTo={pathname}>
        <QuoteRequestView
          access={{ via: "link", token }}
          supplierQuoteNo={supplierQuoteNo}
        />
      </LinkShell>
    );
  }
  return (
    <SignedInShell>
      <QuoteRequestView
        access={{ via: "login" }}
        supplierQuoteNo={supplierQuoteNo}
      />
    </SignedInShell>
  );
}

function QuoteRequestView({
  access: accessProp,
  supplierQuoteNo,
}: {
  access: SupplierAccess;
  supplierQuoteNo: string;
}) {
  const adapter = useSupplierPortalAdapter();
  const capabilities = usePlatformCapabilities();
  const navigate = useNavigate();
  const signedIn = useSupplierSession();
  const notFoundWhy = useNotFoundWhy(
    "quote request",
    QUOTE_REQUEST_WORDING.notFoundWhy,
  );
  const accessKey =
    accessProp.via === "link" ? `link:${accessProp.token}` : "login";
  // One access object per credential, so the read's key and the writes agree.
  const access = useMemo(() => accessProp, [accessKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const {
    data: read,
    error,
    reload,
  } = useLoad(
    () => adapter.getQuoteRequest(access, supplierQuoteNo),
    `${accessKey}|${supplierQuoteNo}`,
  );

  useEffect(() => {
    if (read?.kind === "signed_out") {
      navigate(
        paths.supplierSignIn({
          returnTo: paths.supplierQuoteRequest({ supplierQuoteNo }),
        }),
        { replace: true },
      );
    }
  }, [read, navigate, supplierQuoteNo]);

  if (!read) {
    return error ? (
      <LoadFailed
        what="this quote request"
        message={mutationErrorMessage(
          error,
          "Something went wrong reaching Pollards.",
        )}
        onRetry={reload}
      />
    ) : (
      <LoadingCard what="the quote request" />
    );
  }

  if (read.kind === "link_not_valid") {
    return (
      <Card>
        <EmptyState
          title={QUOTE_REQUEST_WORDING.linkNotValidTitle}
          action={
            <Button asChild>
              <Link to={paths.supplierSignIn()}>{ACTION_WORDING.signIn}</Link>
            </Button>
          }
        >
          <p>{QUOTE_REQUEST_WORDING.linkNotValidWhy}</p>
          <p>{QUOTE_REQUEST_WORDING.linkNotValidNext}</p>
        </EmptyState>
      </Card>
    );
  }
  if (read.kind === "not_found") {
    return (
      <Card>
        <EmptyState
          title={QUOTE_REQUEST_WORDING.notFoundTitle}
          action={
            <Button asChild variant="outline">
              <Link to={paths.supplierQuoteRequests()}>
                {QUOTE_REQUEST_WORDING.list.heading}
              </Link>
            </Button>
          }
        >
          <p>{notFoundWhy}</p>
        </EmptyState>
      </Card>
    );
  }
  if (read.kind === "signed_out") {
    return <LoadingCard what="the quote request" />;
  }

  // After a write: read the request again, and the session's counts with it.
  const afterWrite = async (write: Promise<void>): Promise<void> => {
    await write;
    reload();
    signedIn?.refresh();
  };

  return (
    <QuoteRequestBody
      page={read.page}
      via={access.via}
      writes={{
        send: (answer, quoteDocument) =>
          afterWrite(
            adapter.sendQuotePrices(
              access,
              supplierQuoteNo,
              answer,
              quoteDocument,
            ),
          ),
        decline: (reason, requestId) =>
          afterWrite(
            adapter.declineQuoteRequest(
              access,
              supplierQuoteNo,
              reason,
              requestId,
            ),
          ),
        download: async (file) => {
          const blob = await adapter.downloadQuoteRequestFile(
            access,
            supplierQuoteNo,
            file.id,
          );
          capabilities.downloadBlob(file.filename, blob);
        },
      }}
    />
  );
}
